// Тексты простого кабинета репетитора (24.10.2026): экран «Готово!» после
// анкеты, главная кабинета из четырёх блоков, вкладки «Поделиться», экран
// «Изменить анкету». Название карты — одно на язык: AZ «Evə yaxın»,
// RU «Рядом с домом», EN «Near home». В AZ и EN нет кириллицы
// (scripts/verify-tutor-cabinet-simple.mts). AZ — на вычитку.
import type { SiteLang } from "@/components/site/SiteChrome";

const SITE = "https://yolutap.com";

export const TUTORS_ME_EDIT_PATH: Record<SiteLang, string> = {
  ru: "/ru/tutors/me/edit",
  az: "/az/tutors/me/edit",
  en: "/en/tutors/me/edit",
};

/** «Учёба за рубежом»: старт подбора на языке страницы (маршруты *study-abroad_.start, az.xaricde-tehsil_.start). */
const ABROAD_START: Record<SiteLang, string> = {
  ru: "/study-abroad/start",
  az: "/az/xaricde-tehsil/start",
  en: "/en/study-abroad/start",
};

/** Ссылки «Поделиться» с ?ref=<код анкеты>. ref только запоминается у сессии теста и подбора. */
export const testShareLink = (lang: SiteLang, code: string) => `${SITE}/start?lang=${lang}&pkg=basic&ref=${code}`;
export const abroadShareLink = (lang: SiteLang, code: string) => `${SITE}${ABROAD_START[lang]}?ref=${code}`;

export type ShareTab = "colleague" | "test" | "abroad";

export type CabinetHomeCopy = {
  // экран «Готово!»
  sentH1: string;
  sentNoEmail: string;
  sentShareLead: string;
  sentWhatsapp: string;
  seeMap: string;
  myCabinet: string;
  sentCabinetNote: string;
  // блок 1
  profileTitle: string;
  statusOnMap: string;
  statusPending: string;
  statusPaused: string;
  statusPausedUntil: (date: string) => string;
  clicks: (n: number) => string;
  open: string;
  edit: string;
  pause: string;
  unpause: string;
  pendingEdit: (date: string) => string;
  tasksTitle: string;
  taskFill: string;
  // блок 2
  shareTitle: string;
  tabs: Record<ShareTab, string>;
  shareText: Record<Exclude<ShareTab, "colleague">, (link: string) => string>;
  shareNote: string;
  whatsapp: string;
  copy: string;
  copied: string;
  // экран «Изменить анкету»
  editTitle: string;
  editRule: string;
  backToCabinet: string;
  // удаление
  deleteLink: string;
  deleteAsk: string;
  deleteYes: string;
  deleteNo: string;
  /** В подтверждении удаления: скрыть с карты вместо удаления (полная пауза, 26.10.2026). */
  deleteHide: string;
};

export const CABINET_HOME_COPY: Record<SiteLang, CabinetHomeCopy> = {
  ru: {
    sentH1: "Готово!",
    sentNoEmail: "Мы проверим анкету и напишем вам перед публикацией.",
    sentShareLead: "Знаете других репетиторов? Отправьте им ссылку на карту «Рядом с домом».",
    sentWhatsapp: "Отправить коллегам в WhatsApp",
    seeMap: "Посмотреть карту",
    myCabinet: "Мой кабинет",
    sentCabinetNote: "Кабинет уже открыт на этом устройстве: анкету можно изменить в любой момент.",
    profileTitle: "Моя анкета",
    statusOnMap: "На карте ✓",
    statusPending: "На проверке",
    statusPaused: "Пауза",
    statusPausedUntil: (d) => `Пауза до ${d}`,
    clicks: (n) => `Родители нажали WhatsApp или «Позвонить» за 30 дней: ${n}`,
    open: "Открыть",
    edit: "Изменить",
    pause: "Пауза",
    unpause: "Снять паузу",
    pendingEdit: (d) => `Изменения от ${d} ждут проверки.`,
    tasksTitle: "Нужно ваше действие",
    taskFill: "Заполнить",
    shareTitle: "Поделиться",
    tabs: { colleague: "Коллеге", test: "Ученикам: карьерный тест", abroad: "Учёба за рубежом — бесплатно" },
    shareText: {
      test: (l) => `Карьерный тест Yolutap: ~30 минут и личный отчёт о профессиях, которые вам подходят. Пройти: ${l}`,
      abroad: (l) => `Учёба за рубежом: бесплатный подбор вузов и стран под ваш бюджет и оценки от Yolutap. Начать: ${l}`,
    },
    shareNote: "Ссылки только показывают, кто пришёл от вас. На тест, отчёт и подбор они не влияют.",
    whatsapp: "WhatsApp",
    copy: "Копировать",
    copied: "Скопировано",
    editTitle: "Изменить анкету",
    editRule: "Цена, предметы и формат меняются сразу; имя, фото и адрес — после проверки.",
    backToCabinet: "← В кабинет",
    deleteLink: "Удалить анкету",
    deleteAsk: "Анкета будет удалена с карты. Вы уверены?",
    deleteYes: "Да, удалить",
    deleteNo: "Нет, вернуться",
    deleteHide: "Временно скрыть",
  },
  az: {
    sentH1: "Hazırdır!",
    sentNoEmail: "Anketi yoxlayıb dərc etməzdən əvvəl sizə yazacağıq.",
    sentShareLead: "Başqa repetitorlar tanıyırsınız? Onlara «Evə yaxın» xəritəsinin linkini göndərin.",
    sentWhatsapp: "Həmkarlara WhatsApp-da göndər",
    seeMap: "Xəritəyə baxın",
    myCabinet: "Kabinetim",
    sentCabinetNote: "Kabinet bu cihazda artıq açıqdır: anketi istənilən vaxt dəyişə bilərsiniz.",
    profileTitle: "Mənim anketim",
    statusOnMap: "Xəritədədir ✓",
    statusPending: "Yoxlanılır",
    statusPaused: "Fasilə",
    statusPausedUntil: (d) => `${d} tarixinədək fasilə`,
    clicks: (n) => `Valideynlər 30 gündə WhatsApp və ya «Zəng et» düyməsini basıb: ${n}`,
    open: "Aç",
    edit: "Dəyiş",
    pause: "Fasilə",
    unpause: "Fasiləni bitir",
    pendingEdit: (d) => `${d} tarixli dəyişikliklər yoxlanılır.`,
    tasksTitle: "Sizdən bir addım lazımdır",
    taskFill: "Doldur",
    shareTitle: "Paylaş",
    tabs: { colleague: "Həmkara", test: "Şagirdlərə: karyera testi", abroad: "Xaricdə təhsil — pulsuz" },
    shareText: {
      test: (l) => `Yolutap karyera testi: ~30 dəqiqə və sizə uyğun peşələr haqqında şəxsi hesabat. Keçmək üçün: ${l}`,
      abroad: (l) => `Xaricdə təhsil: büdcənizə və qiymətlərinizə uyğun universitet və ölkə seçimi, Yolutap-dan pulsuz. Başlamaq üçün: ${l}`,
    },
    shareNote: "Linklər yalnız kimin sizdən gəldiyini göstərir. Testə, hesabata və seçimə təsir etmir.",
    whatsapp: "WhatsApp",
    copy: "Kopyala",
    copied: "Kopyalandı",
    editTitle: "Anketi dəyiş",
    editRule: "Qiymət, fənlər və format dərhal dəyişir; ad, foto və ünvan — yoxlamadan sonra.",
    backToCabinet: "← Kabinetə",
    deleteLink: "Anketi sil",
    deleteAsk: "Anket xəritədən silinəcək. Əminsiniz?",
    deleteYes: "Bəli, sil",
    deleteNo: "Xeyr, geri qayıt",
    deleteHide: "Müvəqqəti gizlət",
  },
  en: {
    sentH1: "Done!",
    sentNoEmail: "We'll review your profile and write to you before it goes live.",
    sentShareLead: "Know other tutors? Send them a link to the \"Near home\" map.",
    sentWhatsapp: "Send to colleagues on WhatsApp",
    seeMap: "See the map",
    myCabinet: "My dashboard",
    sentCabinetNote: "Your dashboard is already open on this device: you can change your profile any time.",
    profileTitle: "My profile",
    statusOnMap: "On the map ✓",
    statusPending: "Under review",
    statusPaused: "Paused",
    statusPausedUntil: (d) => `Paused until ${d}`,
    clicks: (n) => `Parents tapped WhatsApp or "Call" in the last 30 days: ${n}`,
    open: "Open",
    edit: "Edit",
    pause: "Pause",
    unpause: "Resume",
    pendingEdit: (d) => `Changes from ${d} are waiting for review.`,
    tasksTitle: "We need your action",
    taskFill: "Fill in",
    shareTitle: "Share",
    tabs: { colleague: "To a colleague", test: "Students: career test", abroad: "Study abroad — free" },
    shareText: {
      test: (l) => `Yolutap career test: ~30 minutes and a personal report on careers that fit you. Take it here: ${l}`,
      abroad: (l) => `Study abroad: a free Yolutap match of universities and countries for your budget and grades. Start here: ${l}`,
    },
    shareNote: "The links only show who came from you. They don't affect the test, the report or the matching.",
    whatsapp: "WhatsApp",
    copy: "Copy",
    copied: "Copied",
    editTitle: "Edit profile",
    editRule: "Price, subjects and format change right away; name, photo and address — after review.",
    backToCabinet: "← To dashboard",
    deleteLink: "Delete profile",
    deleteAsk: "Your profile will be removed from the map. Are you sure?",
    deleteYes: "Yes, delete",
    deleteNo: "No, go back",
    deleteHide: "Hide for now",
  },
};
